import { emailValidator, textLengthValidator, urlValidator } from '@helpers';
import {
  AnswerStatuses, AuthService, ICoordinates, IMeeting, 
  IMeetingDeleteDto, IUser, MeetingRequest, TCalendarType, TMeetingSetup, 
  TUserForMeetingPlaner, createMeeting, getDateInUserTimezone as inUserTimezone, getMeetingFromDto,
  getMeetingToDto, isCanEditMeeting, roundUpTime, transformDateToUtcString,
} from '@utils';
import {
  addMilliseconds, endOfDay, getHours, getMinutes, isWithinInterval, setHours, setMinutes,
} from 'date-fns';
import {
  action, computed, makeObservable, observable, reaction,
  runInAction,
} from 'mobx';
import { Inject, Service } from 'typedi';
import { MembersStore } from '../../../../store';
import { ProfileViewModel } from '../../../../views';

@Service()
export class MeetingViewModel {
  @Inject(() => MeetingRequest)
  private meetingRequest: MeetingRequest;

  @Inject(() => ProfileViewModel)
  private profileViewModel: ProfileViewModel;

  @Inject(() => MembersStore)
  private membersStore: MembersStore;

  reviver = (key: keyof IMeeting, value: IMeeting[keyof IMeeting]) => {
    if (key === 'startDate' || key === 'endDate' || key === 'oldStartDate' || key === 'oldEndDate') return new Date(value as string);
    if (key === 'meetingSetup') return { ...(value as TMeetingSetup), expDate: new Date((value as TMeetingSetup).expDate) };
    return value;
  };

  /**
   * Объект встречи. По умолчанию равен null, до инициализации или получении данных из реквеста
   */
  @observable meetingData: IMeeting | null = JSON.parse(sessionStorage.getItem('meeting'), this.reviver) ?? null;

  @observable.ref moreMeetingInformation: IMeeting | null = null;

  @observable.ref isShowMoreMeetingInformation = false;

  @observable.ref popupCoordinates: ICoordinates | null = null;

  @observable isEdit = (AuthService.getUserId() === this.meetingData?.initiator?.id || this.meetingData?.initiator === undefined)
    && JSON.parse(sessionStorage.getItem('editable')) as boolean;

  @observable isMeetingCycled = false;

  @observable draggingMeetingId: number | null = null;

  @observable isOpenedFromFilterPage = false;

  @observable isMeetingUpdated = JSON.parse(sessionStorage.getItem('isMeetingUpdated')) as boolean ?? false;

  constructor() {
    makeObservable(this);

    reaction(
      () => this.meetingData,
      (meeting) => {
        sessionStorage.setItem('meeting', JSON.stringify(meeting));
      },
    );

    reaction(
      () => this.isMeetingUpdated,
      (isMeetingUpdated) => {
        sessionStorage.setItem('isMeetingUpdated', JSON.stringify(isMeetingUpdated));
      },
    );
  }

  /**
   * Создает шаблон встречи
   */
  @action initMeeting = (
    dataBase?: Partial<IMeeting>,
  ) => {
    this.meetingData = createMeeting(dataBase);
    this.isMeetingUpdated = false;
    this.isMeetingCycled = this.meetingData.intervalType !== 'WITHOUT_REPEAT';
    this.draggingMeetingId = null;
  };

  @action saveTime = (startTime: Date, endTime: Date) => {
    this.meetingData.startDate = startTime;
    this.meetingData.endDate = endTime;
  }

  @action endMeting = () => (
    this.meetingRequest.endMeeting({ 
      meetingId: this.meetingData.id, 
      oldEndMeetingDate: transformDateToUtcString(this.meetingData.oldEndDate, this.profileViewModel.minutesOffset),
      oldStartMeetingDate: transformDateToUtcString(this.meetingData.oldStartDate, this.profileViewModel.minutesOffset),
    })
  );

  /**
   * Функция подгрузки митинга по id
   * @param meetingId Id митинга, который надо загрузить
   */
  loadMeetingById = (meetingId: number, calendarType?: TCalendarType, start?: Date, end?: Date) => (
    this.getMeetingById(meetingId, calendarType).then((resp) => {
      this.initMeeting(getMeetingFromDto(resp.data, this.profileViewModel.minutesOffset));
      //TODO: Удалить когда бекенд будет присылать корректные даты для цикл. встреч
      if (this.meetingData.intervalType !== 'WITHOUT_REPEAT' && start && end) {
        this.meetingData.startDate = this.meetingData.oldStartDate = start;
        this.meetingData.endDate = this.meetingData.oldEndDate = end;
        sessionStorage.setItem('meeting', JSON.stringify(this.meetingData));
      }
    })
  );

  @action createOuterMembers = () => {
    this.onUpdateMeeting('outerMembers', this.meetingData.outerMembers.concat({
      id: Date.now(), name: '', email: '' 
    }))
  }

  @computed get isOuterMembersValid() {
    return this.meetingData.outerMembers.every((outerMember) => emailValidator(outerMember.email));
  }

  @action getChangeOuterMembersFunctions = (id: number) => {
    const onChangeEmail = (email: string) => {
      const newOuterMembers = this.meetingData.outerMembers.map(member => (
        member.id === id ? {...member, email} : member
      ));
      this.onUpdateMeeting('outerMembers', newOuterMembers)
    }
    const onChangeName = (name: string) => {
      const newOuterMembers = this.meetingData.outerMembers.map(member => (
        member.id === id ? {...member, name} : member
      ));
      this.onUpdateMeeting('outerMembers', newOuterMembers)
    }

    return { onChangeEmail, onChangeName };
  }

  @action deleteOuterMember = (id: number) => {
    this.onUpdateMeeting('outerMembers', this.meetingData.outerMembers.filter(elem => elem.id !== id))
  }

  /**
   * Отказ от предложения встречи
   * @param confirmationId id подтверждения встречи
   * @returns Промис выполнения запроса.
   */
  @action cancelTimeOffer = (confirmationId: number) => (
    this.meetingRequest.cancelTimeOffer(confirmationId).then(() => runInAction(() => {
      this.meetingData.meetingConfirmation = this.meetingData.meetingConfirmation.map((elem) => (
        elem.meetingConfirmationId !== confirmationId
          ? elem
          : { ...elem, answerStatus: AnswerStatuses.COUNTER_OFFER_REJECTED }
      ));
      sessionStorage.setItem('meeting', JSON.stringify(this.meetingData)); // обновление митинга)
    }))
  );

  answerTimeOffer = (isAccept: boolean, confirmationId: number) => (isAccept
    ? this.meetingRequest.acceptTimeOffer(confirmationId)
    : this.cancelTimeOffer(confirmationId));

  /**
   * Установка окна редактирования в режим просмотра или редактирования
   * @param value true - редактирование, false - просмотр
   */
  @action setEditable = (value: boolean) => {
    sessionStorage.setItem('editable', `${value}`);
    this.isEdit = value;
  };

  /** Получить MeetingDto по id */
  getMeetingById = (id: number, calendarType: TCalendarType) => this.meetingRequest.getMeeting(id, calendarType);

  /**
   * @param property - поле объекта встречи, которое надо обновить
   * @param value - значение поля
   */
  @action onUpdateMeeting = (property: keyof IMeeting, value: IMeeting[keyof IMeeting]) => {
    this.isMeetingUpdated = true;
    this.meetingData = { ...this.meetingData, [property]: value };
  };

  /**
   * При обновлении поля format сбрасываются выбранная комната и ссылка
   */
  @action resetFormat = () => {
    const newMeetingData = { ...this.meetingData };
    delete newMeetingData.link;
    delete newMeetingData.room;
    this.meetingData = newMeetingData;
  };

  @action createMeeting = () => this.meetingRequest.createMeeting(
    getMeetingToDto(this.meetingData, this.profileViewModel.minutesOffset),
  );

  @action updateMeeting = (meeting?: IMeeting) =>
    this.meetingRequest.updateMeeting(
      getMeetingToDto(meeting ? meeting : this.meetingData, this.profileViewModel.minutesOffset),
    );

  /**
   * @param meetingId - id встречи, которую хотим удалить
   */
  @action deleteMeeting = (meetingId: number) => this.meetingRequest.deleteMeeting(meetingId);

  /**
   * @param meeting - встреча, которую хотим удалить
   */
  @action deleteRecurringMeeting = (meeting: IMeetingDeleteDto) => this.meetingRequest.deleteRecurringMeeting(meeting);

  @action resetMeetingData = () => {
    this.meetingData = null;
    this.isMeetingUpdated = false;
    this.isMeetingCycled = null;
  };

  /**
   * Проверка на наличие ссылки или выбранной комнаты (в зависимости от формата встречи) и присутвие от 2х человек
   */
  @computed get isMeetingValid() {
    const date = new Date();
    return (
      ((this.meetingData.format === 'ONLINE'
        && (!this.meetingData.link || (urlValidator(this.meetingData.link)
          && textLengthValidator(this.meetingData.link)))) || this.meetingData.room)
      && (this.meetingData.meetingName && textLengthValidator(this.meetingData.meetingName, 55))
      && this.meetingData.startDate.getTime() > date.getTime()
      && this.meetingData.startDate.getTime() < this.meetingData.endDate.getTime()
      && (this.meetingData.intervalType === 'WITHOUT_REPEAT' 
        || endOfDay(this.meetingData.meetingSetup.expDate).getTime() >= this.meetingData.endDate.getTime())
      && textLengthValidator(this.meetingData.description, 1024)
      && !(this.meetingData.intervalType === 'ANOTHER'
        && this.meetingData.meetingSetup.intervalFormat === 'WEEK'
        && this.meetingData.meetingSetup.daysOfWeek.length === 0)
      && this.isOuterMembersValid
    );
  }

  @action closeMoreMeetingInformationModal = () => {
    this.isShowMoreMeetingInformation = false;
  };

  @action setMoreMeetingInformation = (meeting: IMeeting) => {
    this.moreMeetingInformation = meeting;
    this.isShowMoreMeetingInformation = true;
  };

  @action openAddNewMeetingForm = (today: Date) => {
    this.setIsOpenedFromFilterPage(false);
    this.setEditable(true);
    const date = today ?? new Date();
    const currentHour = getHours(date);
    const currentMinutes = getMinutes(date);
    date.setSeconds(0);
    date.setMilliseconds(0);
    const startDate = roundUpTime(setMinutes(setHours(date, currentHour), currentMinutes + 10));
    const endDate = roundUpTime(setMinutes(setHours(date, currentHour), currentMinutes + 40));
    this.initMeeting({
      startDate,
      endDate,
      oldStartDate: startDate,
      oldEndDate: endDate,
    });
  };

  @action getSavedMembers = () => {
    const map = new Map(JSON.parse(localStorage.getItem('savedMembers')) as TUserForMeetingPlaner);
    if (this.meetingData.id === null && map.size > 0) {
      localStorage.removeItem('savedMembers');
      const members: IUser[] = [];

      for (const user of map.values()) {
        members.push({
          id: user.id,
          img: user.img,
          corporateEmail: user.corporateEmail,
          fullName: user.fullName,
          position: user.position,
          isActive: user.isActive,
          remoteWorkType: user.remoteWorkType,
        });
      }

      this.meetingData = { ...this.meetingData, members };
    }
  };

  @action getSavedData = () => {
    if (this.meetingData.id === null && localStorage.getItem('savedDate')) {
      const startDate = new Date(localStorage.getItem('savedDate'));
      startDate.setHours(this.meetingData.startDate.getHours());
      startDate.setMinutes(this.meetingData.startDate.getMinutes());
      const diff = +this.meetingData.endDate - +this.meetingData.startDate;
      const endDate = addMilliseconds(startDate, diff);
      localStorage.removeItem('savedDate');
      const meetingSetup: TMeetingSetup = {...this.meetingData.meetingSetup, expDate: endDate}

      this.meetingData = { ...this.meetingData, startDate, endDate, meetingSetup };
    }
  }

  @action setPopupCoordinates = (coordinates: ICoordinates) => {
    this.popupCoordinates = coordinates;
  };

  isUserInMembers = (user: IUser) => !!this.meetingData.members.find((member) => member.id === user.id);

  getDateInUserTimezone = (date: Date) => inUserTimezone(date.toString(), this.profileViewModel.minutesOffset);

  @computed get isMeetingGoing() {
    return isWithinInterval(
      new Date(), 
      {
        start: this.meetingData.startDate, 
        end: this.meetingData.endDate
      },
    );
  }

  @action updateSelectedMembers = (memberId: number) => {
    this.meetingData.members.find((user) => memberId === user.id)
      ? this.onUpdateMeeting('members', this.meetingData.members.filter((user) => user.id !== memberId))
      : this.onUpdateMeeting('members', this.meetingData.members.concat(this.membersStore.getMemberById(memberId)));
  };

  @action setMeetingDraggingId = (value: number | null) => {
    this.draggingMeetingId = value;
  };

  @action setIsOpenedFromFilterPage = (value: boolean) => {
    this.isOpenedFromFilterPage = value;
  };

  get isMeetingCanEdited() {
    return isCanEditMeeting(this.meetingData);
  }
}
